/**
 * SSO / auth-mode helpers (MP-2328).
 *
 * These bridge the new marketplace-api auth surface (per-marketplace AuthMode, account-status,
 * OIDC popup login, and the legacy-ticket exchange) back into the app's existing ticket/refreshToken
 * session model. All marketplace-api calls are same-origin relative URLs, matching the existing
 * `/api/configuration` fetch.
 */

import { getConfig } from './index';
import { getMarketplaceApiBase, getMarketplaceApiOrigin } from './apiBase';

/**
 * Per-marketplace login behavior, mirrors marketplace-api's AuthMode enum (serialized by name).
 */
export const AUTH_MODES = {
  CURRENT: 'Current',
  MIXED_CHOICE: 'MixedChoice',
  MIXED_NO_CHOICE: 'MixedNoChoice',
  FORCED: 'Forced',
  /**
   * Everyone signs in through the marketplace's own IdP. Unlike the mixed and forced modes there is no
   * migration fork, so the email screen is skipped entirely — asking for an email would only discard it,
   * since the account-status check reads the CIAM migration table this marketplace never populates.
   */
  SSO: 'Sso',
};

/**
 * @returns {string} the marketplace's configured AuthMode, defaulting to Current (preserves the
 * legacy ias-connect experience when the field is absent, e.g. static fallback config).
 */
export function getAuthMode() {
  const mode = getConfig('authMode');
  return Object.values(AUTH_MODES).includes(mode) ? mode : AUTH_MODES.CURRENT;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
  return typeof email === 'string' && EMAIL_REGEX.test(email.trim());
}

// sessionStorage key holding the redirect-flow handoff nonce across the top-level round trip.
const SSO_NONCE_KEY = 'mp-sso-nonce';

/**
 * @returns {boolean} true if the app is running inside an iframe. A cross-origin parent makes
 * `window.top` access throw, which also means we're embedded.
 */
export function isEmbedded() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * Ask marketplace-api whether an email has been migrated to SSO. Response is a uniform
 * `{ migrated }` shape; anything else is treated as "not migrated".
 * @param {string} email
 * @returns {Promise<boolean>}
 */
export async function fetchAccountStatus(email) {
  const res = await fetch(`${getMarketplaceApiBase()}/api/account/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(`account_status_failed_${res.status}`);
  }
  const data = await res.json();
  return Boolean(data && data.migrated);
}

/**
 * Migration outcomes returned by `POST /api/account/migrate`.
 */
export const MIGRATION_STATUS = {
  /** The account is provisioned in Auth0 and the user has been emailed a link to set a password. */
  VERIFICATION_SENT: 'verification_sent',
  /** The account was already migrated; send the user straight to SSO. */
  ALREADY_MIGRATED: 'already_migrated',
};

/**
 * Start SSO migration for an email: marketplace-api provisions the account in the CIAM platform, which
 * triggers Auth0 to email a set-password link.
 *
 * The response is deliberately uniform for real and unknown addresses (it cannot be used to check
 * whether an account exists), so a `verification_sent` result means "we've done what we can" rather
 * than "an email definitely went out".
 *
 * @param {string} email
 * @returns {Promise<string>} one of MIGRATION_STATUS
 */
export async function startMigration(email) {
  const res = await fetch(`${getMarketplaceApiBase()}/api/account/migrate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(`migration_failed_${res.status}`);
  }
  const data = await res.json();
  return (data && data.status) || MIGRATION_STATUS.VERIFICATION_SENT;
}

/**
 * Exchange a native marketplace-api user JWT for a legacy AWG session ticket + refresh token.
 * @param {string} accessToken native access token from the OIDC popup handoff
 * @returns {Promise<{ticket: string, refreshToken: string}>}
 */
export async function exchangeLegacyTicket(accessToken) {
  const res = await fetch(`${getMarketplaceApiBase()}/api/oauth/legacy-ticket`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error(`legacy_ticket_failed_${res.status}`);
  }
  return res.json();
}

function generateHandoffNonce() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Launch the OIDC popup login and resolve with the native token pair once the callback posts its
 * `mp-auth` handoff message.
 *
 * Security: the callback posts to our (opener) origin — passed as `client_origin` and validated by
 * the server against its allow-list — and stamps the message with the `handoff_nonce` we generated.
 * The message's SENDER origin (`event.origin`) is marketplace-api's origin, so we validate against
 * that (same as the opener origin in production, where the API is same-origin). We accept only
 * messages from the API origin, typed `mp-auth`, carrying the matching nonce.
 *
 * @param {{returnPath?: string}} [options]
 * @returns {Promise<{accessToken: string, refreshToken: string}>}
 */
export function beginSsoPopup(options = {}) {
  const { returnPath } = options;
  return new Promise((resolve, reject) => {
    const handoffNonce = generateHandoffNonce();
    const clientOrigin = window.location.origin; // opener origin the callback posts TO
    const apiOrigin = getMarketplaceApiOrigin(); // origin the callback posts FROM

    let url =
      `${getMarketplaceApiBase()}/api/oauth/oidc/login?flow=popup` +
      `&client_origin=${encodeURIComponent(clientOrigin)}` +
      `&handoff_nonce=${encodeURIComponent(handoffNonce)}`;
    if (returnPath) {
      url += `&return_url=${encodeURIComponent(returnPath)}`;
    }

    const popup = window.open(url, 'mp-sso-login', 'width=520,height=720');
    if (!popup) {
      reject(new Error('popup_blocked'));
      return;
    }

    let settled = false;
    const cleanup = () => {
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closeTimer);
    };

    const onMessage = event => {
      if (event.origin !== apiOrigin) return;
      const data = event.data;
      if (!data || data.type !== 'mp-auth' || data.nonce !== handoffNonce) return;

      cleanup();
      try {
        popup.close();
      } catch (e) {
        /* ignore */
      }

      if (data.access_token) {
        resolve({ accessToken: data.access_token, refreshToken: data.refresh_token });
      } else {
        reject(new Error('sso_no_token'));
      }
    };

    window.addEventListener('message', onMessage);

    // Detect the user closing the popup without completing login.
    const closeTimer = setInterval(() => {
      if (!settled && popup.closed) {
        cleanup();
        reject(new Error('popup_closed'));
      }
    }, 500);
  });
}

/**
 * Start OIDC login via a top-level redirect (used when NOT embedded in an iframe). Navigates the whole
 * tab to the IdP; the callback returns to `returnPath` with the tokens in the URL fragment, handled on
 * load by completeSsoRedirectIfPresent().
 *
 * Note: marketplace-api normalizes `return_url` to a relative path, so the callback's redirect resolves
 * against the API's origin. This is correct in production (API is same-origin) but not in cross-origin
 * local dev — use the popup path there.
 *
 * @param {{returnPath?: string}} [options]
 */
export function beginSsoRedirect(options = {}) {
  const { returnPath } = options;
  const handoffNonce = generateHandoffNonce();
  window.sessionStorage.setItem(SSO_NONCE_KEY, handoffNonce);

  const returnUrl = returnPath || `${window.location.pathname}${window.location.search}`;
  const url =
    `${getMarketplaceApiBase()}/api/oauth/oidc/login?flow=redirect` +
    `&handoff_nonce=${encodeURIComponent(handoffNonce)}` +
    `&return_url=${encodeURIComponent(returnUrl)}`;

  window.location.assign(url);
}

/**
 * If the current URL carries an OIDC redirect-flow handoff fragment, complete login: validate the
 * nonce, exchange the access token for a legacy ticket, and navigate into the app. The fragment is
 * always stripped from the URL. No-op (resolves false) when there is no handoff fragment.
 *
 * @returns {Promise<boolean>} true if a handoff was processed (navigation is underway)
 */
export async function completeSsoRedirectIfPresent() {
  const hash = window.location.hash || '';
  if (hash.indexOf('access_token=') === -1) {
    return false;
  }

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const accessToken = params.get('access_token');
  const state = params.get('state');
  const expectedNonce = window.sessionStorage.getItem(SSO_NONCE_KEY);
  window.sessionStorage.removeItem(SSO_NONCE_KEY);

  // Strip the fragment (with the tokens) from the URL regardless of outcome.
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

  if (!accessToken) {
    return false;
  }
  if (expectedNonce && state !== expectedNonce) {
    throw new Error('sso_state_mismatch');
  }

  const legacyTicket = await exchangeLegacyTicket(accessToken);
  redirectWithTicket(legacyTicket);
  return true;
}

/**
 * Launch SSO login using the right mechanism for the context: a popup when embedded in an iframe
 * (a top-level redirect isn't possible/safe there), otherwise a top-level redirect (better UX, no
 * popup blockers). In the popup case this resolves after exchanging for the legacy ticket and
 * navigating; in the redirect case the tab navigates away immediately.
 *
 * @param {{returnPath?: string}} [options]
 */
export async function startSsoLogin(options = {}) {
  if (isEmbedded()) {
    const { accessToken } = await beginSsoPopup(options);
    const legacyTicket = await exchangeLegacyTicket(accessToken);
    redirectWithTicket(legacyTicket);
    return;
  }
  beginSsoRedirect(options);
}

/**
 * Hand a freshly-minted legacy ticket to the existing session bootstrap by navigating to the same
 * `?mpId=&ticket=&refreshToken=` URL shape the ias-connect redirect used. The app's `loadApiKey`
 * picks the params up, stores them, and strips them from the URL — no new token-storage path needed.
 * @param {{ticket: string, refreshToken: string}} legacyTicket
 */
export function redirectWithTicket(legacyTicket) {
  const { ticket, refreshToken } = legacyTicket;
  const mpId = getConfig('marketplaceId');
  window.location =
    `${window.location.origin}/?mpId=${encodeURIComponent(mpId)}` +
    `&ticket=${encodeURIComponent(ticket)}` +
    `&refreshToken=${encodeURIComponent(refreshToken)}`;
}
